import * as Sentry from "@sentry/react"
import { useQuery } from "@tanstack/react-query"
import GraduationHat from "assets/images/graduationHat.png"
import { auth } from "auth"
import { getConfig } from "backoffice-api/src/apiConfig"
import { FetchError } from "fetcher"
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"
import { ErrorBoundary } from "react-error-boundary"
import { BACKOFFICE_API_URL } from "../../../env"
import { useCurrentCompanyContext } from "../../AppShell/CurrentCompanyProvider"
import { AIChat } from "../index"

export type AIAssistant = {
  id: string
  type: "ai_assistants"
  attributes: {
    name: string
    tagline: string | null
    starter_questions: string[]
    avatar: {
      id: string
      head: string
    }
  }
}

export type ChatConfig = {
  id: string
  title: string
  tagline: string | null
  questionSuggestions: string[]
  assistantAvatar: string
}

type AIChatContextType = {
  openChat: () => void
  chatConfig: ChatConfig | null
  isAIChatEnabled: boolean
  allChatConfigs: ChatConfig[]
  selectAssistant: (id: string) => void
}

export const AIChatContext = createContext<AIChatContextType | undefined>(
  undefined
)

export const useAIChatContext = () => {
  const context = useContext(AIChatContext)
  if (!context) {
    throw Error(
      "AIChatContext not present. You are probably outside the AI chat context provider. Check that the component using the context is within the boundaries of the context."
    )
  }

  return context
}

export const AIChatContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const {
    currentCompany: { nameKey },
  } = useCurrentCompanyContext()

  // Read initial value from session storage,
  // This allows us to remember if the chat was open between hard refreshes,
  // but closes it between sessions.
  const [isOpen, setIsOpen] = useState(
    sessionStorage.getItem("isAIChatOpen") === "true"
  )

  const { data: assistants = [] } = useQuery({
    queryKey: ["assistants", nameKey],
    queryFn: () => getAssistants(nameKey!),
    enabled: Boolean(nameKey),
  })

  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null
  )

  const allChatConfigs: ChatConfig[] = assistants.map((assistant) => ({
    id: assistant.id,
    title: assistant.attributes.name,
    tagline: assistant.attributes.tagline ?? null,
    questionSuggestions: assistant.attributes.starter_questions ?? [],
    assistantAvatar: assistant.attributes.avatar.head,
  }))

  const chatConfig =
    allChatConfigs.find((config) => config.id === selectedAssistantId) ??
    allChatConfigs[0] ??
    null

  const isAIChatEnabled = Boolean(chatConfig)

  useEffect(() => {
    sessionStorage.setItem("isAIChatOpen", JSON.stringify(isOpen))
  }, [isOpen])

  const openChat = () => {
    if (isOpen) return
    setIsOpen(true)
  }

  return (
    <AIChatContext.Provider
      value={{
        openChat,
        chatConfig,
        isAIChatEnabled,
        allChatConfigs,
        selectAssistant: setSelectedAssistantId,
      }}
    >
      <>
        {children}

        {/* A crashing chat hides itself instead of blanking the page. Tagged
            `error_boundary` so an alert can fire on it. */}
        <ErrorBoundary
          fallback={<></>}
          resetKeys={[chatConfig?.id]}
          onError={(error) =>
            Sentry.captureException(error, {
              level: "fatal",
              tags: { feature: "fastAIAssistant", error_boundary: "ai_chat" },
            })
          }
        >
          <AIChat
            key={chatConfig?.id}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          />
        </ErrorBoundary>
      </>
    </AIChatContext.Provider>
  )
}

const getAssistants = async (nameKey: string): Promise<AIAssistant[]> => {
  const { accessToken } = await auth.getActiveSession()
  const { apiToken } = await getConfig()

  const url = `${BACKOFFICE_API_URL}/api/v3/companies/${nameKey}/ai_assistants`

  const request = new Request(url, {
    method: "GET",
    headers: {
      Accept: "application/vnd.api+json",
      Authorization: `Bearer ${accessToken}`,
      "API-Token": apiToken,
    },
  })

  const response = await fetch(request)

  if (response.status === 404) {
    return []
  }

  if (!response.ok) {
    // We create FetchError for more accurate logging.
    // Sentry is configured to ignore some FetchErrors based on response status.
    const error = new FetchError({
      url,
      method: "GET",
      status: response.status,
      message: `Failed to fetch AI assistants for company with status code ${response.status}`,
      cause: undefined,
      text: undefined,
      json: undefined,
    })
    Sentry.captureException(error, { tags: { feature: "fastAIAssistant" } })

    // Fail silently - it's not crash worthy
    return []
  }

  const { data } = await response.json()

  return data ?? []
}

export const MockedAIChatContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const mockedAIChatContext = {
    openChat: () => {},
    chatConfig: {
      id: "mocked",
      title: "Assistant",
      tagline: null,
      questionSuggestions: [],
      assistantAvatar: GraduationHat,
    },
    isAIChatEnabled: false,
    allChatConfigs: [],
    selectAssistant: () => {},
  }

  return (
    <AIChatContext.Provider value={mockedAIChatContext}>
      <>
        {children}

        <AIChat isOpen={false} onClose={() => {}} />
      </>
    </AIChatContext.Provider>
  )
}
